// /dev-reviews — 開發者審核(A1;common/08 §3.1 帳號核可 + §5 model 發布)。
// 平台 admin 僅有的兩個審核點集中在此頁:上=組織開通申請佇列(個人開發者自助
// 開通、免審核,不進佇列;common/08 §3.1 業主定案 2026-07-12)、下=model 發布
// 佇列(sparse review-index)。駁回一律要理由(GM-13);其餘開發迴圈零審核。
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { canAdminWrite, useAuth } from '../../auth/AuthContext';
import { CopyableId } from '../../components/CopyableId';
import { EmptyState, ErrorState, LoadingState } from '../../components/StateBlock';
import { formatTimestampUs } from '../../lib/format';
import {
  DevApplication, ModelReview,
  approveApplication, listApplications, listModelReviews, rejectApplication, reviewModel,
} from './devReviewApi';
import DeveloperAccountPanel from './DeveloperAccountPanel';
import '../_shared/shared.css';
import './devreview.css';

type Busy = { id: string; action: string } | null;

// 申請類型中文化(devReviewApi DevApplication.type: personal|org_owner|org_member);
// 純顯示,未知值退原值 + title。
const TYPE_LABEL: Record<string, string> = {
  personal: '個人', org_owner: '組織擁有者', org_member: '組織成員',
};

// 機型類別中文化(common/08 §S display_name/category → device/03 device_class);
// display-only、不作授權依據,未知值退原值 + title。
const CATEGORY_LABEL: Record<string, string> = {
  camera: '攝影機', sensor: '感測器', doorbell: '門鈴',
  plug: '智慧插座', nvr: 'NVR 錄影主機', dvr: 'DVR 錄影主機',
};

export default function DevReviewPage() {
  const { role } = useAuth();
  const writable = canAdminWrite(role); // cs-readonly 直連本頁時:核可/駁回收起,佇列仍可檢視
  const [apps, setApps] = useState<DevApplication[] | null>(null);
  const [models, setModels] = useState<ModelReview[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<Busy>(null);
  const [rejecting, setRejecting] = useState<string | null>(null); // id awaiting a reason
  const [reason, setReason] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const noticeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const reload = useCallback(async () => {
    setErr(null);
    try {
      // 待審 state 的 canonical 值是 'applied'（後端 developer_profiles 生命週期 applied→approved；
      // ListDeveloperProfiles 對 state 精確比對）。舊值 'pending' 永遠比不中 → 佇列空（admin#11）。
      const [a, m] = await Promise.all([listApplications('applied'), listModelReviews()]);
      setApps(a);
      setModels(m);
    } catch (e) {
      setErr(e instanceof Error ? e.message : '載入失敗');
    }
  }, []);

  useEffect(() => { void reload(); }, [reload]);

  // notice 3.5 秒後自動消失,避免操作成功訊息一直卡在頁首(不再靠使用者手動關閉)。
  useEffect(() => {
    if (!notice) return;
    if (noticeTimer.current) clearTimeout(noticeTimer.current);
    noticeTimer.current = setTimeout(() => setNotice(null), 3500);
    return () => { if (noticeTimer.current) clearTimeout(noticeTimer.current); };
  }, [notice]);

  const act = async (label: string, id: string, fn: () => Promise<unknown>) => {
    setBusy({ id, action: label });
    setErr(null);
    try {
      await fn();
      setNotice(`${label}完成：${id}`);
      setRejecting(null);
      setReason('');
      await reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : `${label}失敗`);
    } finally {
      setBusy(null);
    }
  };

  if (err && apps === null) return <ErrorState error={err} onRetry={() => void reload()} />;
  if (apps === null || models === null) return <LoadingState />;

  const totalPending = apps.length + models.length;
  const bothEmpty = apps.length === 0 && models.length === 0;

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">
            開發者審核
            {totalPending > 0 ? (
              <span className="pill pill-warn dr-title-pill">
                共 {totalPending} 筆待審
              </span>
            ) : (
              <span className="pill pill-good dr-title-pill">
                目前無待審
              </span>
            )}
          </h1>
          <p className="page-subtitle">
            平台僅有的兩個審核點：組織（org）開通，與機型發布（含駁回理由，駁回後開發者修正重送）。個人開發者自助開通、免審核，不進此佇列。
          </p>
        </div>
        <button className="btn-ghost" onClick={() => void reload()}>重新整理</button>
      </div>

      {notice && <div className="adm-action-result is-ok" role="status">{notice}</div>}
      {err && <div className="adm-action-result is-err" role="alert">{err}</div>}

      {/* 兩佇列皆空→並排(省垂直留白);任一有資料→全寬堆疊,讓 Model 表得全寬、
          避免 model/歸屬 長 mono id 在窄欄被擠成逐字直排。 */}
      <div className={`dr-queues${bothEmpty ? '' : ' dr-queues--stack'}`}>
        {/* ── 開發者申請 ──(主要行動點:左 accent 條標示這是本頁主佇列) */}
        <section className="card card--primary">
          <div className="section-head">
            <h2 className="section-title">組織開通申請</h2>
            <span className="section-meta">{apps.length} 筆待核可</span>
          </div>
          {apps.length === 0 ? (
            <EmptyState title="目前沒有待核可的申請" detail="組織（org）開通申請出現時會列在這裡。個人開發者自助開通、免審核。" />
          ) : (
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr><th>使用者</th><th>類型</th><th>工作區</th><th>申請時間</th><th>動作</th></tr>
                </thead>
                <tbody>
                  {apps.map((a) => {
                    const key = `app:${a.user_id}`;
                    return (
                      <Fragment key={a.user_id}>
                        <tr data-testid={`app-${a.user_id}`}>
                          <td><CopyableId value={a.user_id} display={a.user_id} /></td>
                          <td><span title={a.type}>{TYPE_LABEL[a.type] ?? a.type}</span>{a.company_info?.name ? `（${a.company_info.name}）` : ''}</td>
                          <td>{a.slug || '—'}</td>
                          <td>{formatTimestampUs(a.applied_at)}</td>
                          <td>
                            {writable ? (
                              <div className="adm-actionbar-btns">
                                <button className="btn-primary btn-sm" disabled={busy !== null}
                                  onClick={() => void act('核可', a.user_id, () => approveApplication(a.user_id))}>
                                  核可
                                </button>
                                <button className="btn-ghost btn-sm dr-reject-btn" disabled={busy !== null}
                                  onClick={() => setRejecting(rejecting === key ? null : (() => { setReason(''); return key; })())}>
                                  駁回…
                                </button>
                              </div>
                            ) : (
                              <span className="text-tertiary">唯讀</span>
                            )}
                          </td>
                        </tr>
                        {rejecting === key && (
                          <tr className="dr-reject-row">
                            <td colSpan={5}>
                              <div className="adm-search-row">
                                <input type="text" value={reason} placeholder="駁回理由（必填，開發者會看到）"
                                  onChange={(e) => setReason(e.target.value)} aria-label="駁回理由" autoFocus />
                                <button className="btn-primary" disabled={!reason.trim() || busy !== null}
                                  onClick={() => void act('駁回', a.user_id, () => rejectApplication(a.user_id, reason.trim()))}>
                                  送出駁回
                                </button>
                                <button className="btn-ghost" onClick={() => setRejecting(null)}>取消</button>
                              </div>
                              {!reason.trim() && (
                                <p className="adm-hint">請填寫理由，開發者會在通知中看到（必填）</p>
                              )}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* ── Model 發布審核 ── */}
        <section className="card">
          <div className="section-head">
            <h2 className="section-title">機型發布審核</h2>
            <span className="section-meta">{models.length} 筆待審核</span>
          </div>
          {models.length === 0 ? (
            <EmptyState title="佇列是空的" detail="開發者送出機型發布申請後，會列在這裡等待審核。" />
          ) : (
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr><th>機型</th><th>名稱／類別</th><th>能力</th><th>歸屬組織</th><th>動作</th></tr>
                </thead>
                <tbody>
                  {models.map((m) => {
                    const key = `model:${m.model}`;
                    return (
                      <Fragment key={m.model}>
                        <tr data-testid={`model-${m.model}`}>
                          <td><CopyableId value={m.model} display={m.model} /></td>
                          <td>{m.display_name}{m.category ? <>・<span title={m.category}>{CATEGORY_LABEL[m.category] ?? m.category}</span></> : ''}</td>
                          <td>
                            {(m.capability ?? []).length > 0 ? (
                              <div className="dr-cap-cell">
                                {(m.capability ?? []).map((c) => (
                                  <span key={c.cluster} className="pill pill-neutral">{c.cluster}</span>
                                ))}
                              </div>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td><CopyableId value={m.owner_scope} display={m.owner_scope} /></td>
                          <td>
                            {writable ? (
                              <div className="adm-actionbar-btns">
                                <button className="btn-primary btn-sm" disabled={busy !== null}
                                  onClick={() => void act('發布核可', m.model, () => reviewModel(m.model, 'approve'))}>
                                  核可發布
                                </button>
                                <button className="btn-ghost btn-sm dr-reject-btn" disabled={busy !== null}
                                  onClick={() => setRejecting(rejecting === key ? null : (() => { setReason(''); return key; })())}>
                                  駁回…
                                </button>
                              </div>
                            ) : (
                              <span className="text-tertiary">唯讀</span>
                            )}
                          </td>
                        </tr>
                        {rejecting === key && (
                          <tr className="dr-reject-row">
                            <td colSpan={5}>
                              <div className="adm-search-row">
                                <input type="text" value={reason} placeholder="駁回理由（必填，開發者會看到）"
                                  onChange={(e) => setReason(e.target.value)} aria-label="駁回理由" autoFocus />
                                <button className="btn-primary" disabled={!reason.trim() || busy !== null}
                                  onClick={() => void act('駁回', m.model, () => reviewModel(m.model, 'reject', reason.trim()))}>
                                  送出駁回
                                </button>
                                <button className="btn-ghost" onClick={() => setRejecting(null)}>取消</button>
                              </div>
                              {!reason.trim() && (
                                <p className="adm-hint">請填寫理由，開發者會在通知中看到（必填）</p>
                              )}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      <DeveloperAccountPanel writable={writable} />

      {/* 兩佇列皆空時,補一塊次要引導卡片,縮短空狀態下的垂直留白 */}
      {bothEmpty && (
        <div className="card card--muted dr-followup-card">
          <h3 className="adm-card-title dr-followup-title">近期已處理</h3>
          <p className="text-secondary dr-followup-text">
            沒有新申請時，可以到 <Link to="/audit">操作紀錄</Link> 查最近的核可／駁回紀錄。
          </p>
        </div>
      )}
    </>
  );
}
